import { getAuditActionLabel } from "@/features/audit-logs/constants/action-labels";
import { REVIEW_STATUS_LABEL } from "@/features/content/lib/content-review-status";
import { getPermissionLabel } from "@/features/permissions/constants/permission-labels";
import { getRoleLabel } from "@/features/roles/constants/role-labels";
import type { AuditLog } from "@/types/audit-log";

const FIELD_LABELS: Record<string, string> = {
  isActive: "الحساب نشط",
  method: "طريقة الدخول",
  email: "البريد الإلكتروني",
  firstName: "الاسم الأول",
  lastName: "اسم العائلة",
  role: "الدور",
  departmentId: "الدائرة",
  reviewStatus: "حالة المراجعة",
  published: "منشور",
  title: "العنوان",
  slug: "الرابط",
  isNews: "خبر",
  version: "الإصدار",
  note: "ملاحظة",
  code: "الرمز",
  name: "الاسم",
  description: "الوصف",
  userId: "المستخدم",
  changed: "التغييرات",
  locales: "اللغات",
};

const METHOD_LABELS: Record<string, string> = {
  otp: "رمز التحقق",
  sso: "تسجيل دخول موحد",
};

const LOCALE_LABELS: Record<string, string> = {
  ar: "العربية",
  en: "الإنجليزية",
};

const RESOURCE_LABELS: Record<string, string> = {
  page: "صفحة",
  user: "مستخدم",
  role: "دور",
  auth: "مصادقة",
};

const GRANTED_LABELS = {
  granted: "ممنوح",
  denied: "مرفوض",
} as const;

export interface AuditPayloadLine {
  label: string;
  value: string;
}

export interface AuditPayloadFormatContext {
  departmentLookup?: Record<string, string>;
}

function parseAuditPayload(value: unknown): unknown {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }
  return value;
}

function formatGranted(value: boolean | undefined): string {
  if (value === undefined) return "غير محدد";
  return value ? GRANTED_LABELS.granted : GRANTED_LABELS.denied;
}

function formatPermissionChanges(value: unknown): string {
  if (!Array.isArray(value)) return String(value ?? "—");

  return value
    .map((item) => {
      if (typeof item !== "object" || item === null) return String(item);
      const change = item as {
        permission?: string;
        granted?: boolean;
        previousGranted?: boolean;
      };
      const label = getPermissionLabel(change.permission ?? "");
      if (change.previousGranted === undefined) {
        return `${label}: ${formatGranted(change.granted)}`;
      }
      return `${label}: ${formatGranted(change.previousGranted)} ← ${formatGranted(change.granted)}`;
    })
    .join("\n");
}

function formatLocalizedValue(value: unknown): string | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }

  const entries = Object.entries(value as Record<string, unknown>).filter(
    ([, fieldValue]) => fieldValue != null && String(fieldValue).trim() !== "",
  );
  if (entries.length === 0) return null;

  return entries
    .map(([locale, fieldValue]) => {
      const localeLabel = LOCALE_LABELS[locale] ?? locale;
      return `${localeLabel}: ${String(fieldValue)}`;
    })
    .join(" · ");
}

function formatArrayValue(key: string, value: unknown[]): string {
  if (key === "locales") {
    return value
      .map((item) => LOCALE_LABELS[String(item)] ?? String(item))
      .join("، ");
  }

  return value.map((item) => String(item)).join("، ");
}

function formatAuditValue(
  key: string,
  value: unknown,
  context?: AuditPayloadFormatContext,
): string {
  if (value === null || value === undefined) return "—";

  switch (key) {
    case "isActive":
      return value ? "نشط" : "غير نشط";
    case "published":
    case "isNews":
      return value ? "نعم" : "لا";
    case "method":
      return METHOD_LABELS[String(value)] ?? String(value);
    case "role":
      return getRoleLabel(String(value));
    case "reviewStatus":
      return REVIEW_STATUS_LABEL[String(value)] ?? String(value);
    case "departmentId":
      return context?.departmentLookup?.[String(value)] ?? String(value);
    case "changed":
      return formatPermissionChanges(value);
    case "title":
    case "slug": {
      const localized = formatLocalizedValue(value);
      return localized ?? String(value);
    }
    case "locales":
      return Array.isArray(value) ? formatArrayValue(key, value) : String(value);
    default:
      if (typeof value === "boolean") return value ? "نعم" : "لا";
      if (Array.isArray(value)) return formatArrayValue(key, value);
      if (typeof value === "object") {
        const localized = formatLocalizedValue(value);
        return localized ?? JSON.stringify(value);
      }
      return String(value);
  }
}

export function formatAuditPayloadLines(
  value: unknown,
  context?: AuditPayloadFormatContext,
): AuditPayloadLine[] {
  const parsed = parseAuditPayload(value);
  if (parsed == null) return [];
  if (typeof parsed !== "object" || Array.isArray(parsed)) {
    return [{ label: "القيمة", value: String(parsed) }];
  }

  return Object.entries(parsed as Record<string, unknown>).map(([key, fieldValue]) => ({
    label: FIELD_LABELS[key] ?? key,
    value: formatAuditValue(key, fieldValue, context),
  }));
}

export function formatAuditResource(
  log: Pick<AuditLog, "resource" | "resourceId">,
  options?: { includeResourceId?: boolean },
): string {
  const includeResourceId = options?.includeResourceId ?? true;
  const resourceLabel = log.resource
    ? (RESOURCE_LABELS[log.resource] ?? log.resource)
    : undefined;
  if (resourceLabel && log.resourceId && includeResourceId) {
    return `${resourceLabel} · ${log.resourceId}`;
  }
  return resourceLabel ?? (includeResourceId ? log.resourceId : undefined) ?? "—";
}

/** Compact summary for dashboard widgets — never surfaces internal ids. */
export function auditLogWidgetResourceLabel(
  log: Pick<AuditLog, "resource">,
): string | undefined {
  if (!log.resource) return undefined;
  return RESOURCE_LABELS[log.resource] ?? log.resource;
}

export function auditLogSummary(log: AuditLog): {
  actionLabel: string;
  resourceLabel: string;
} {
  return {
    actionLabel: getAuditActionLabel(log.action),
    resourceLabel: formatAuditResource(log),
  };
}
